import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PITCH_W, PITCH_H, FIELD_PAD, drawPitch, onGrassReady } from "../utils/pitch";
import { resolveAwayColor } from "../utils/colors";
import "./GamePlayer.css";

const PLAYER_R = 11;
const BALL_R = 5;
const FLASH_DECAY = 0.018;

// pos format from engine: [lateral/fieldWidth, depth/fieldLong]
// lateral: 0=bottom side, 1=top side
// depth:   0=local goal, 0.5=centre, 1=away goal
function makeMockFrames(count = 300) {
  const frames = [];
  let score = [0, 0];
  for (let i = 0; i < count; i++) {
    if (i === 120) score = [1, 0];
    if (i === 230) score = [1, 1];
    const t = i / count;
    frames.push({
      t: i,
      score: [...score],
      ball: [
        0.5 + 0.15 * Math.sin(t * Math.PI * 4),
        0.5 + 0.35 * Math.cos(t * Math.PI * 6),
      ],
      local: [
        { pos: [0.5,  0.05],                                     energy: Math.max(0, 1 - t * 0.6) },
        { pos: [0.25, 0.18],                                     energy: Math.max(0, 1 - t * 0.8) },
        { pos: [0.5,  0.18],                                     energy: Math.max(0, 1 - t * 0.5) },
        { pos: [0.75, 0.18],                                     energy: Math.max(0, 1 - t * 0.9) },
        { pos: [0.3,  0.33 + 0.08 * Math.sin(t * Math.PI * 3)], energy: Math.max(0, 1 - t * 0.7) },
        { pos: [0.7,  0.33 + 0.08 * Math.cos(t * Math.PI * 3)], energy: Math.max(0, 1 - t * 0.4) },
        { pos: [0.5,  0.42],                                     energy: Math.max(0, 1 - t * 0.5) },
      ],
      away: [
        { pos: [0.5,  0.95],                                     energy: Math.max(0, 1 - t * 0.7) },
        { pos: [0.25, 0.82],                                     energy: Math.max(0, 1 - t * 0.5) },
        { pos: [0.5,  0.82],                                     energy: Math.max(0, 1 - t * 0.9) },
        { pos: [0.75, 0.82],                                     energy: Math.max(0, 1 - t * 0.6) },
        { pos: [0.3,  0.67 + 0.08 * Math.cos(t * Math.PI * 3)], energy: Math.max(0, 1 - t * 0.8) },
        { pos: [0.7,  0.67 + 0.08 * Math.sin(t * Math.PI * 3)], energy: Math.max(0, 1 - t * 0.4) },
        { pos: [0.5,  0.58],                                     energy: Math.max(0, 1 - t * 0.6) },
      ],
    });
  }
  return frames;
}

const MOCK_FRAMES = makeMockFrames();

const STEPS_PER_SECOND = 30; // render steps per GAME-SECOND at 1x (smoothness
// dial). A hop a->b renders round((b.t - a.t) * STEPS_PER_SECOND) steps, so the
// viewer plays every hop for its real duration — agnostic to grid/cycle count.
// A 300s match always runs 5 min at 1x whatever the frame count.

function lerp(a, b, t) { return a + (b - a) * t; }

// Ball path within one frame transition. When the next frame carries a
// "bounce" point (wall impact during that cycle), the ball travels
// a -> bounce -> b with time split by segment length so speed is constant.
function lerpBall(a, b, t) {
  const bounce = b.bounce;
  if (!bounce) {
    return [lerp(a.ball[0], b.ball[0], t), lerp(a.ball[1], b.ball[1], t)];
  }
  const d1 = Math.hypot(bounce[0] - a.ball[0], bounce[1] - a.ball[1]);
  const d2 = Math.hypot(b.ball[0] - bounce[0], b.ball[1] - bounce[1]);
  const split = d1 + d2 > 0 ? d1 / (d1 + d2) : 0.5;
  if (t <= split) {
    const u = split > 0 ? t / split : 1;
    return [lerp(a.ball[0], bounce[0], u), lerp(a.ball[1], bounce[1], u)];
  }
  const u = split < 1 ? (t - split) / (1 - split) : 1;
  return [lerp(bounce[0], b.ball[0], u), lerp(bounce[1], b.ball[1], u)];
}

// Blend two display frames for SUB-FRAME interpolation: smooth motion at the
// screen's refresh rate, independent of the 30/s logical step. The caller guards
// against blending across a goal/kickoff snap (score change), so here we just
// lerp positions.
function blendFrame(a, b, t) {
  return {
    ...a,
    high:  lerp(a.high ?? 0, b.high ?? 0, t),
    ball:  lerpBall(a, b, t),
    local: a.local.map((p, j) => ({ pos: [lerp(p.pos[0], b.local[j].pos[0], t), lerp(p.pos[1], b.local[j].pos[1], t)], energy: p.energy })),
    away:  a.away.map((p, j) => ({ pos: [lerp(p.pos[0], b.away[j].pos[0],  t), lerp(p.pos[1], b.away[j].pos[1],  t)], energy: p.energy })),
  };
}

export function interpolateFrames(frames) {
  if (frames.length < 2) return frames;
  const out = [];
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i];
    const b = frames[i + 1];
    // Snap (don't interpolate) across a goal, so players CUT to their base
    // positions instead of sliding there. Two triggers, on purpose:
    //   - the score just changed (a -> b), or
    //   - frame `a` is the goal frame (ev type 2 = "scores"); its next hop is
    //     the kickoff teleport.
    // The score trigger alone covers matches recorded with the current engine
    // (score bump and reset land on the same frame); the ev trigger also fixes
    // older stored replays, recorded when the score changed one frame early.
    const aScored = Array.isArray(a.ev) && a.ev[2] === 2;
    const scored = aScored || b.score[0] !== a.score[0] || b.score[1] !== a.score[1];
    // Each frame carries an ABSOLUTE timestamp t (game-seconds); this hop's
    // duration is b.t - a.t. It renders proportionally many steps so every hop
    // plays at constant real speed, agnostic to grid/cycle count. A former
    // "cut" sub-frame is just a frame at a fractional t, no special case. _real
    // stamps each render frame with its source index (variable steps => can't
    // derive it by division).
    const hop = (typeof b.t === "number" && typeof a.t === "number") ? (b.t - a.t) : 1;
    const steps = Math.max(1, Math.round(hop * STEPS_PER_SECOND));
    for (let f = 0; f < steps; f++) {
      if (scored) {
        out.push({ ...b, _real: i });
      } else {
        const t = f / steps;
        out.push({
          ...a,
          _real: i,
          // goal kick in the air ("high":1 frames): eased 0..1 so the ball
          // grows on launch and settles where it comes down
          high:  lerp(a.high ? 1 : 0, b.high ? 1 : 0, t),
          ball:  lerpBall(a, b, t),
          local: a.local.map((p, j) => ({ pos: [lerp(p.pos[0], b.local[j].pos[0], t), lerp(p.pos[1], b.local[j].pos[1], t)], energy: p.energy })),
          away:  a.away.map((p, j) => ({ pos: [lerp(p.pos[0], b.away[j].pos[0],  t), lerp(p.pos[1], b.away[j].pos[1],  t)], energy: p.energy })),
        });
      }
    }
  }
  out.push({ ...frames[frames.length - 1], _real: frames.length - 1 });
  return out;
}

function drawScore(ctx, w, h, score) {
  const [ls, as_] = score;
  ctx.save();
  ctx.font = `bold ${Math.round(h * 0.18)}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 8;
  ctx.fillStyle = "rgba(255,255,255,0.28)";
  ctx.fillText(`${ls}  –  ${as_}`, w / 2, h / 2);
  ctx.restore();
}

function drawEndOverlay(ctx, w, h, { color, text }) {
  ctx.save();

  // Dark tinted overlay
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);

  // Main result text — shrink to fit the width (portrait pitch is narrow)
  let endFs = Math.round(h * 0.14);
  ctx.font = `bold ${endFs}px sans-serif`;
  const endMaxW = w * 0.88;
  const endTw = ctx.measureText(text).width;
  if (endTw > endMaxW) endFs = Math.max(8, Math.floor(endFs * endMaxW / endTw));
  ctx.font = `bold ${endFs}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.9)";
  ctx.shadowBlur = 24;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, w / 2, h / 2);

  ctx.restore();
}

function drawGoalFlash(ctx, w, h, { color, opacity, text }) {
  ctx.save();

  // White flash overlay
  ctx.fillStyle = `rgba(255,255,255,${opacity * 0.45})`;
  ctx.fillRect(0, 0, w, h);

  // GOAL! text in team colour — shrink to fit the width (portrait pitch is narrow)
  let goalFs = Math.round(h * 0.22);
  ctx.font = `bold ${goalFs}px sans-serif`;
  const goalMaxW = w * 0.88;
  const goalTw = ctx.measureText(text).width;
  if (goalTw > goalMaxW) goalFs = Math.max(8, Math.floor(goalFs * goalMaxW / goalTw));
  ctx.font = `bold ${goalFs}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 16;
  ctx.globalAlpha = opacity;
  ctx.fillStyle = color;
  ctx.fillText(text, w / 2, h / 2);

  ctx.restore();
}

// --- Confetti (goal celebration) --------------------------------------------
// Screen-space particle burst raining from the top edge. Orientation-agnostic
// (works for both the landscape match and the portrait autotest viewer).
function makeConfetti(w, h, colors) {
  const parts = [];
  const n = Math.round(Math.max(50, Math.min(130, w / 5)));
  for (let i = 0; i < n; i++) {
    parts.push({
      x: Math.random() * w,
      y: -Math.random() * h * 0.4,          // start just above the top edge
      vx: (Math.random() - 0.5) * 2.4,
      vy: 1.6 + Math.random() * 2.8,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.32,
      size: 4 + Math.random() * 5,
      color: colors[(Math.random() * colors.length) | 0],
    });
  }
  return parts;
}

function stepConfetti(parts, h) {
  const alive = [];
  for (const p of parts) {
    p.x += p.vx;
    p.y += p.vy;
    p.vy += 0.04;          // gravity
    p.vx *= 0.995;         // mild air drag
    p.rot += p.vrot;
    if (p.y < h + 24) alive.push(p);   // drop once well below the pitch
  }
  return alive;
}

function drawConfetti(ctx, parts) {
  ctx.save();
  ctx.globalAlpha = 0.95;
  for (const p of parts) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.restore();
}

// Engine energy is a 3-level bucket per player: 2 = high, 1 = medium, 0 = low.
// These are the sim's speed tiers (run / middle / walk). Fresh (high) players
// get NO ring; a ring only appears once they tire: yellow at medium, red at low.
function energyRingColor(level) {
  if (level >= 2) return null;             // high → no ring
  return level >= 1 ? "rgb(250,204,21)"    // medium → yellow
                    : "rgb(239,68,68)";    // low → red
}

// Single initial: the first letter of the name, uppercased. One centered glyph
// reads bigger and shimmers less than two at this marker size.
function initials(name) {
  if (!name) return "";
  const c = name.trim()[0];
  return c ? c.toUpperCase() : "";
}

// Pre-rendered initials (rasterized once, blitted) — see earlier notes; now the
// viewer interpolates per-frame (smooth motion) so the sprite shouldn't judder.
const _tagCache = new Map();
function getTagSprite(tag, fontSize) {
  const key = tag + "|" + fontSize;
  let c = _tagCache.get(key);
  if (c) return c;
  const fs = fontSize * 3;   // 3x supersample → finer resample when blitted sub-pixel = less shimmer
  c = document.createElement("canvas");
  c.width = Math.ceil(fs * 2.4);
  c.height = Math.ceil(fs * 1.6);
  const g = c.getContext("2d");
  g.font = `bold ${fs}px sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillStyle = "#fff";
  g.fillText(tag, c.width / 2, c.height / 2);
  _tagCache.set(key, c);
  return c;
}

function drawPlayer(ctx, nx, ny, fieldX, fieldY, fieldW, fieldH, fillColor, energy, name, r = PLAYER_R) {
  const x = fieldX + nx * fieldW;
  const y = fieldY + ny * fieldH;

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = fillColor;
  ctx.fill();

  // Black outline — defines the disc against the grass AND sits BETWEEN the body
  // colour and the energy ring, so the two never muddy (e.g. a red energy ring
  // on a red team reads fine with black between). Always drawn.
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.strokeStyle = "#141414";
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Energy ring — only once the player is tiring. Fresh (high-energy) players
  // get NO ring: less visual noise, and the ring appearing IS the fatigue
  // signal (yellow = tiring/slower, red = exhausted). Independent of the
  // inspector highlight ring, which is a separate stroke at r + 7.
  const ringColor = energyRingColor(energy ?? 2);
  if (ringColor) {
    ctx.beginPath();
    ctx.arc(x, y, r + 1, 0, Math.PI * 2);
    ctx.strokeStyle = ringColor;
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Two-letter initials inside the disc (pre-rendered sprite, blitted).
  const tag = initials(name);
  if (tag && r >= 5) {
    const spr = getTagSprite(tag, Math.round(r * 1.1));
    const dw = spr.width / 3, dh = spr.height / 3;   // /3 undoes the 3x supersample
    ctx.drawImage(spr, x - dw / 2, y - dh / 2, dw, dh);
  }
}

// Inspector: high-contrast ring around the player being debugged. A dark halo
// behind a bright ring makes it pop on the green grass (plain amber blended in).
function drawHighlight(ctx, nx, ny, fieldX, fieldY, fieldW, fieldH, r) {
  const x = fieldX + nx * fieldW;
  const y = fieldY + ny * fieldH;
  ctx.save();
  // dark backing ring for contrast against the pitch
  ctx.beginPath();
  ctx.arc(x, y, r + 7, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(0,0,0,0.7)";
  ctx.lineWidth = 6;
  ctx.stroke();
  // bright ring on top
  ctx.beginPath();
  ctx.arc(x, y, r + 7, 0, Math.PI * 2);
  ctx.strokeStyle = "#fde047";           // amber — matches the fired-action chip
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();
}

// Map field coords (lateral, depth) to normalized screen coords [nx, ny].
//  horizontal: depth→X (local goal=left), lateral→Y  (broadcast landscape)
//  vertical:   lateral→X, (1-depth)→Y  — 90° rotation, LOCAL (depth 0) at the
//              BOTTOM attacking upward; rotation (not reflection) => no mirror.
function project(lat, dep, vertical) {
  return vertical ? [lat, 1 - dep] : [dep, lat];
}

// Comic impact star for a successful tackle — a quick "clash!" burst.
function drawTackleSpark(ctx, x, y, r) {
  const spikes = 10;
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const ang = (Math.PI * i) / spikes - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    const px = x + Math.cos(ang) * rad;
    const py = y + Math.sin(ang) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = "#ffd23f";
  ctx.strokeStyle = "#c0392b";
  ctx.lineWidth = 1.5;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawFrame(ctx, frame, w, h, localColor, awayColor, highlightIndex = null, vertical = false, localNames = [], awayNames = []) {
  const fx = FIELD_PAD, fy = FIELD_PAD;
  const fw = w - 2 * FIELD_PAD, fh = h - 2 * FIELD_PAD;
  const scale = w / (vertical ? PITCH_H : PITCH_W);   // w is the SHORT axis when vertical
  const playerR = Math.max(4, Math.round(PLAYER_R * scale));

  frame.local.forEach(({ pos: [lat, dep], energy }, i) => {
    const [nx, ny] = project(lat, dep, vertical);
    if (highlightIndex === i) drawHighlight(ctx, nx, ny, fx, fy, fw, fh, playerR);
    drawPlayer(ctx, nx, ny, fx, fy, fw, fh, localColor, energy, localNames[i], playerR);
  });
  frame.away.forEach(({ pos: [lat, dep], energy }, i) => {
    const [nx, ny] = project(lat, dep, vertical);
    drawPlayer(ctx, nx, ny, fx, fy, fw, fh, awayColor, energy, awayNames[i], playerR);
  });

  const [blat, bdep] = frame.ball;
  const [bnx, bny] = project(blat, bdep, vertical);
  const bx = fx + bnx * fw;
  const by = fy + bny * fh;
  const high = frame.high || 0; // goal kick in the air: 0..1

  if (high > 0.05) {
    // ground shadow lagging below the ball — the gap reads as height
    ctx.beginPath();
    ctx.ellipse(bx + BALL_R * 1.2 * high, by + BALL_R * 2.8 * high,
      BALL_R * 0.9, BALL_R * 0.5, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fill();
  }

  // When a player is carrying it (ball overlaps a player, and not airborne) the
  // ball goes slightly transparent and DROPS its border, so it blends with the
  // carrier instead of sitting on top as a hard opaque disc. Loose/flying ball:
  // fully opaque with its border.
  const airborne = high > 0.05;
  const nearPlayer = (arr) => arr.some(({ pos: [lat, dep] }) => {
    const [pnx, pny] = project(lat, dep, vertical);
    return Math.hypot((fx + pnx * fw) - bx, (fy + pny * fh) - by) < playerR;
  });
  const carried = !airborne && (nearPlayer(frame.local) || nearPlayer(frame.away));

  ctx.save();
  ctx.globalAlpha = carried ? 0.5 : 1;
  ctx.beginPath();
  // airborne ball grows up to +100% (2x) so the flight is clearly visible
  ctx.arc(bx, by, BALL_R * (1 + 1.0 * high), 0, Math.PI * 2);
  ctx.fillStyle = "#fff";
  ctx.fill();
  if (!carried) {
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.restore();

  // Tackle spark: a comic impact star at the ball when a steal lands (ev type 3).
  if (Array.isArray(frame.ev) && frame.ev[2] === 3) {
    drawTackleSpark(ctx, bx, by, BALL_R * 2.6);
  }
}

export default function GamePlayer({
  frames = MOCK_FRAMES,
  localColor       = "#e07b00",
  awayColor        = "#3b82f6",
  awayAltColor     = "#22c55e",
  perspective      = "local",   // "local" | "away"
  autoPlay         = false,
  onEnd            = null,
  onEvent          = null,      // when set, current event is reported here (and the on-pitch ticker is hidden)
  localPlayerNames = [],        // player names indexed by engine player index
  awayPlayerNames  = [],
  highlightIndex   = null,      // inspector: local player index to ring, or null
  onFrame          = null,      // inspector: reports the REAL (decision) frame index
  blockAdvance     = false,     // inspector slow mode: hold the current frame while narrating
  vertical         = false,     // portrait pitch (test viewer): local team at the bottom
  maxHeight        = null,      // cap the canvas height (px) — e.g. the modal's usable height
}) {
  const { t } = useTranslation();
  const resolvedAwayColor = resolveAwayColor(localColor, awayColor, awayAltColor);
  const displayFrames = useMemo(() => interpolateFrames(frames), [frames]);
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const offscreenRef = useRef(null);
  const indexRef     = useRef(0);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const rafRef      = useRef(null);
  const flashRafRef = useRef(null);
  const lastRef     = useRef(null);
  const goalFlash    = useRef(null); // { color, opacity, text }
  const confettiRef  = useRef([]);   // goal confetti particles (screen space)
  const [endOverlay, setEndOverlay] = useState(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const controlsTimerRef = useRef(null);
  const wasPlayingRef = useRef(false);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const blockAdvanceRef = useRef(blockAdvance);
  blockAdvanceRef.current = blockAdvance;
  const FPS = 30;
  const [speed, setSpeed] = useState(1);

  const [tickerEvent, setTickerEvent] = useState(null);
  const tickerTimerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // --- SOUND (disabled, resume when audio feature is ready) ---
  // const audioCtxRef    = useRef(null);
  // const kickBufRef     = useRef(null);
  // const prevEvRef      = useRef(null);
  // const lastSoundEvRef = useRef(null);
  // const [muted, setMuted] = useState(false);
  // const mutedRef       = useRef(false);
  //
  // useEffect(() => {
  //   const ctx = new (window.AudioContext || window.webkitAudioContext)();
  //   audioCtxRef.current = ctx;
  //   fetch(`${process.env.PUBLIC_URL}/sounds/kick.m4a`)
  //     .then((r) => r.arrayBuffer())
  //     .then((buf) => ctx.decodeAudioData(buf))
  //     .then((decoded) => { kickBufRef.current = decoded; })
  //     .catch((e) => console.warn("Audio load failed:", e));
  //   const unlock = () => { if (ctx.state === "suspended") ctx.resume(); };
  //   document.addEventListener("pointerdown", unlock, { once: true });
  //   return () => { ctx.close(); document.removeEventListener("pointerdown", unlock); };
  // }, []);
  //
  // const playKick = useCallback(() => {
  //   const ctx = audioCtxRef.current;
  //   const buf = kickBufRef.current;
  //   if (!ctx || !buf || ctx.state !== "running" || mutedRef.current) return;
  //   const src = ctx.createBufferSource();
  //   src.buffer = buf;
  //   src.connect(ctx.destination);
  //   src.start();
  // }, []);
  // --- END SOUND ---

  // Ticker: show event label (team, player, type) for 2 s.
  // Event format: [team, playerIndex, type] — types: 0=has ball, 1=shoot, 2=goal, 3=tackle, 4=pass
  //
  // Sound strategy (for when audio is re-enabled):
  // - Tackle (3) and pass (4): fire on the rising edge using prevEvRef to detect transition.
  // - Shoot (1): look INTERP_STEPS ahead and fire early; lastSoundEvRef deduplicates
  //   the 30 interpolated look-ahead frames.
  useEffect(() => {
    const ev = displayFrames[index]?.ev;
    if (ev) {
      clearTimeout(tickerTimerRef.current);
      setTickerEvent(ev);
      tickerTimerRef.current = setTimeout(() => setTickerEvent(null), 2000);
      // Sound calls go here (prevEvRef / lastSoundEvRef / playKick)
    }
  }, [index, displayFrames]);

  // Report the current event to the parent (resolved label + team + type), so a
  // host can show it OFF the pitch (e.g. in the header→pitch gap). Mirrors the
  // ticker lifecycle. When onEvent is set, the on-pitch overlay is hidden.
  useEffect(() => {
    if (!onEvent) return;
    if (!tickerEvent) { onEvent(null); return; }
    const [team, playerIdx, type] = tickerEvent;
    const names = team === 0 ? localPlayerNames : awayPlayerNames;
    onEvent({ team, label: names[playerIdx] || `#${playerIdx}`, type });
  }, [tickerEvent, onEvent, localPlayerNames, awayPlayerNames]);

  const handleTap = () => {
    // if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    setControlsVisible(true);
    clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  };

  // Fullscreen support (not available on iOS Safari — graceful degradation)
  // On mobile: lock to landscape when entering, unlock when leaving.
  useEffect(() => {
    const onChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs) window.screen.orientation?.unlock?.();
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().then(() => {
        // Small delay — some browsers need a moment after fullscreen before accepting the lock.
        // Portrait for the vertical (test) pitch, landscape for the wide match view.
        setTimeout(() => {
          window.screen.orientation?.lock?.(vertical ? "portrait" : "landscape").catch(() => {});
        }, 100);
      }).catch((err) => {
        console.warn("Fullscreen request failed:", err);
      });
    } else {
      document.exitFullscreen?.();
    }
  }, [vertical]);

  useEffect(() => { indexRef.current = index; }, [index]);

  // Report the REAL (decision) frame to the inspector. Frames have variable
  // step counts (per-frame dt), so the source index is read from the _real tag
  // interpolateFrames stamped on each render frame, not derived by division.
  useEffect(() => {
    if (!onFrameRef.current) return;
    const real = Math.min(frames.length - 1, displayFrames[index]?._real ?? 0);
    onFrameRef.current(real);
  }, [index, frames.length, displayFrames]);

  const render = useCallback((idx, flash, frac = 0) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.width, h = canvas.height;
    if (w === 0 || h === 0) return; // canvas not sized yet (transient during resize)
    const ctx = canvas.getContext("2d");
    const oc = offscreenRef.current;
    if (oc && oc.width > 0 && oc.height > 0) ctx.drawImage(oc, 0, 0);
    else drawPitch(ctx, w, h, vertical);
    const a = displayFrames[idx];
    if (a) {
      // Sub-frame interpolation toward the next frame → smooth at the screen's
      // refresh rate, independent of the 30/s logical step. Skip across a
      // goal/kickoff snap (score change) so nothing slides across the pitch.
      const b = displayFrames[idx + 1];
      const f = (frac > 0 && b && b.score[0] === a.score[0] && b.score[1] === a.score[1])
        ? blendFrame(a, b, frac) : a;
      // vertical uses an HTML scoreboard (below); the on-pitch number is landscape-only
      if (!vertical) drawScore(ctx, w, h, f.score);
      drawFrame(ctx, f, w, h, localColor, resolvedAwayColor, highlightIndex, vertical, localPlayerNames, awayPlayerNames);
    }
    if (flash && flash.opacity > 0) drawGoalFlash(ctx, w, h, flash);
    if (confettiRef.current.length) drawConfetti(ctx, confettiRef.current);
    if (endOverlay) drawEndOverlay(ctx, w, h, endOverlay);
  }, [displayFrames, endOverlay, localColor, resolvedAwayColor, highlightIndex, vertical, localPlayerNames, awayPlayerNames]);

  const buildOffscreen = useCallback((w, h) => {
    const oc = document.createElement("canvas");
    oc.width = w; oc.height = h;
    drawPitch(oc.getContext("2d"), w, h, vertical);
    offscreenRef.current = oc;
  }, [vertical]);

  // Responsive canvas + offscreen pitch rebuild on resize or grass load
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = (width) => {
      // In fullscreen there's no nav/padding — use full viewport height
      const fs = !!document.fullscreenElement;
      // caller may cap the height (e.g. the modal's usable height); else leave room for nav
      const maxH = fs ? window.innerHeight : (maxHeight ?? window.innerHeight - 100);
      // aspect w:h — landscape uses PITCH_W:PITCH_H, portrait swaps them
      const aspW = vertical ? PITCH_H : PITCH_W;
      const aspH = vertical ? PITCH_W : PITCH_H;
      const wByWidth = Math.round(fs ? width : Math.min(width, aspW));
      const hByWidth = Math.round(wByWidth * aspH / aspW);
      let w, h;
      if (hByWidth > maxH && maxH > 0) {
        h = maxH;
        w = Math.round(h * aspW / aspH);
      } else {
        w = wByWidth;
        h = hByWidth;
      }
      const canvas = canvasRef.current;
      if (!canvas || w <= 0 || h <= 0) return; // skip degenerate sizes during layout
      canvas.width = w;
      canvas.height = h;
      buildOffscreen(w, h);
      render(indexRef.current, goalFlash.current);
    };

    const unsubGrass = onGrassReady(() => {
      const canvas = canvasRef.current;
      if (canvas && canvas.width > 0 && canvas.height > 0) buildOffscreen(canvas.width, canvas.height);
      render(indexRef.current, goalFlash.current);
    });

    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => handleResize(entries[0].contentRect.width));
    });
    observer.observe(container);

    return () => { observer.disconnect(); if (unsubGrass) unsubGrass(); };
  }, [buildOffscreen, render, vertical, maxHeight]);

  // Re-render whenever index or endOverlay changes
  // Static render (paused / scrub / initial / end). During PLAY the loop below
  // owns rendering with sub-frame interpolation, so skip here to avoid snapping
  // back to the exact frame and killing the smoothing.
  useEffect(() => { if (!playing) render(index, goalFlash.current); }, [index, render, endOverlay, playing]);

  // Detect goal between two consecutive frame indices; pauses playback and fades then resumes
  // Fill the sky with confetti in BOTH teams' colours.
  const spawnConfetti = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width || !canvas.height) return;
    confettiRef.current = makeConfetti(canvas.width, canvas.height, [localColor, resolvedAwayColor]);
  }, [localColor, resolvedAwayColor]);

  const detectGoal = useCallback((prevIdx, nextIdx) => {
    const prev = displayFrames[prevIdx];
    const next = displayFrames[nextIdx];
    if (!prev || !next) return false;
    const [pl, pa] = prev.score;
    const [nl, na] = next.score;
    if (nl > pl) { goalFlash.current = { color: "rgb(224,123,0)", opacity: 1, text: t("game.goal") }; spawnConfetti(); return true; }
    if (na > pa) { goalFlash.current = { color: "rgb(59,130,246)", opacity: 1, text: t("game.goal") }; spawnConfetti(); return true; }
    return false;
  }, [displayFrames, t, spawnConfetti]);

  // Flash fade loop — fades overlay then resumes playback if it was playing
  const startFlashFade = useCallback((resumeAfter = false) => {
    cancelAnimationFrame(flashRafRef.current);
    const tick = (idx) => () => {
      const f = goalFlash.current;
      const flashAlive = f && f.opacity > 0;
      const confettiAlive = confettiRef.current.length > 0;
      // Hold the celebration until BOTH the flash has faded and the confetti has
      // rained off the pitch, then resume playback where it paused.
      if (!flashAlive && !confettiAlive) {
        goalFlash.current = null;
        if (resumeAfter) setPlaying(true);
        return;
      }
      if (flashAlive) goalFlash.current = { ...f, opacity: Math.max(0, f.opacity - FLASH_DECAY) };
      if (confettiAlive) confettiRef.current = stepConfetti(confettiRef.current, canvasRef.current?.height ?? 0);
      render(idx, goalFlash.current);
      flashRafRef.current = requestAnimationFrame(tick(idx));
    };
    setIndex((i) => { flashRafRef.current = requestAnimationFrame(tick(i)); return i; });
  }, [render]);

  // Main playback loop
  useEffect(() => {
    if (!playing) { cancelAnimationFrame(rafRef.current); return; }
    // if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();

    const step = (ts) => {
      // Inspector slow mode: hold on the current frame while the panel narrates
      // this decision. Keep the loop alive so it resumes the moment it clears.
      if (blockAdvanceRef.current) {
        lastRef.current = ts;
        render(indexRef.current, goalFlash.current, 0);
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const interval = 1000 / (FPS * speed);
      if (!lastRef.current || ts - lastRef.current >= interval) {
        lastRef.current = ts;
        // Advance the logical index via the REF (kept in sync) and mirror it to
        // state. Using the ref lets the render below target the SAME frame this
        // very tick; React's state lag would otherwise interpolate the wrong hop.
        const i = indexRef.current;
        const next = i + 1;
        if (next >= displayFrames.length) {
          setPlaying(false);
          const [ls, as_] = displayFrames[i].score;
          const iWin = perspective === "local" ? ls > as_ : as_ > ls;
          const iLose = perspective === "local" ? ls < as_ : as_ < ls;
          setEndOverlay(iWin
            ? { color: "rgba(0,80,0,0.72)",    text: t("game.win")  }
            : iLose
            ? { color: "rgba(100,0,0,0.72)",   text: t("game.lose") }
            : { color: "rgba(30,30,30,0.75)",  text: t("game.draw") });
          if (onEndRef.current) onEndRef.current();
        } else {
          const scored = detectGoal(i, next);
          indexRef.current = next;
          setIndex(next);
          if (scored) {
            wasPlayingRef.current = true;
            setPlaying(false);
            startFlashFade(true);
          }
        }
      }
      // Render EVERY rAF, interpolating toward the next frame by how far into the
      // current logical step we are → smooth motion at the screen's refresh rate.
      const frac = lastRef.current ? Math.min(1, (ts - lastRef.current) / interval) : 0;
      render(indexRef.current, goalFlash.current, frac);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, speed, displayFrames, detectGoal, startFlashFade, t, setEndOverlay, perspective, render]);

  const handleScrub = (e) => {
    const next = Number(e.target.value);
    setPlaying(false);
    setIndex(next);
    // Scrubbing is a SEEK, not live play: never fire the goal celebration when
    // the jump crosses a goal (that was the bug — confetti on fast-forward past
    // a goal). Drop any lingering confetti and fade an active flash.
    confettiRef.current = [];
    if (goalFlash.current?.opacity > 0) startFlashFade(false);
  };

  return (
    <div className="gp" ref={containerRef}>
      <div className={`gp-player${controlsVisible ? " gp--controls-visible" : ""}`} onPointerDown={handleTap}>
      <canvas ref={canvasRef} className="gp-canvas" />
      {!onEvent && tickerEvent && (() => {
        const [team, playerIdx, type] = tickerEvent;
        const names = team === 0 ? localPlayerNames : awayPlayerNames;
        const label = names[playerIdx] || `#${playerIdx}`;
        return (
          <div className="gp-ticker">
            <span
              className="gp-ticker-dot"
              style={{ background: team === 0 ? localColor : resolvedAwayColor }}
            />
            <span className="gp-ticker-text">
              {label} {t(`game.ev${type}`)}{type === 3 ? " 💥" : ""}
            </span>
          </div>
        );
      })()}
      <div className="gp-controls">
        <button className="gp-btn" onClick={() => { setIndex(0); setPlaying(false); goalFlash.current = null; confettiRef.current = []; setEndOverlay(null); }}>⏮</button>
        <button className="gp-btn" onClick={() => setPlaying((p) => !p)}>
          {playing ? "⏸" : "▶"}
        </button>
        <button className="gp-btn gp-speed-btn" onClick={() => setSpeed((s) => (s % 3) + 1)}>{speed}×</button>
        {/* <button className="gp-btn" onClick={() => { const m = !muted; setMuted(m); mutedRef.current = m; }}>{muted ? "🔇" : "🔊"}</button> */}
        <input
          className="gp-scrubber"
          type="range"
          min={0}
          max={displayFrames.length - 1}
          value={index}
          onChange={handleScrub}
        />
        {document.fullscreenEnabled && (
          <button className="gp-btn gp-fs-btn" onClick={toggleFullscreen} title={isFullscreen ? t("game.exitFullscreen") : t("game.fullscreen")}>
            {isFullscreen ? "⊠" : "⛶"}
          </button>
        )}
      </div>
      </div>
    </div>
  );
}
